import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSyncedTab } from '../../../hooks/useSyncedTab';
import adminApi from '../../../services/adminApi';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';

const STOCK_STATUS = {
  OUT_OF_STOCK: 'out_of_stock',
  LOW_STOCK: 'low_stock',
  NORMAL: 'normal',
};

function getStockStatus(quantity, reorderPoint) {
  if (quantity <= 0) return STOCK_STATUS.OUT_OF_STOCK;
  if (reorderPoint && quantity <= reorderPoint) return STOCK_STATUS.LOW_STOCK;
  return STOCK_STATUS.NORMAL;
}

function StatusBadge({ quantity, reorderPoint }) {
  const status = getStockStatus(quantity, reorderPoint);
  if (status === STOCK_STATUS.OUT_OF_STOCK) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        Out of Stock
      </span>
    );
  }
  if (status === STOCK_STATUS.LOW_STOCK) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
        Low Stock
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
      In Stock
    </span>
  );
}

function SummaryCard({ label, value, color, sub }) {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
  };
  return (
    <div className={`rounded-lg border p-4 flex flex-col gap-1 ${colorMap[color]}`}>
      <span className="text-2xl font-bold">{value}</span>
      <span className="text-sm font-medium">{label}</span>
      {sub && <span className="text-xs opacity-70">{sub}</span>}
    </div>
  );
}

const PAGE_SIZE = 25;

const StockOverview = () => {
  const [activeTab, setActiveTab] = useSyncedTab('inventory');

  // Shared: locations loaded once
  const [allLocations, setAllLocations] = useState([]);
  const [warehouseMap, setWarehouseMap] = useState({});

  // ── Inventory stock state ──
  const [stockLevels, setStockLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const searchTimer = useRef(null);

  // ── Asset stock state ──
  const [assetLevels, setAssetLevels] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetError, setAssetError] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetDebouncedSearch, setAssetDebouncedSearch] = useState('');
  const [assetBranchFilter, setAssetBranchFilter] = useState('');
  const [assetWarehouseFilter, setAssetWarehouseFilter] = useState('');
  const [assetPagination, setAssetPagination] = useState({ page: 1, total: 0, pages: 0 });
  const assetSearchTimer = useRef(null);

  // Derived location/warehouse lists
  const branches = useMemo(() => allLocations.filter(l => l.type !== 'warehouse'), [allLocations]);
  const warehouses = useMemo(() => allLocations.filter(l => l.type === 'warehouse'), [allLocations]);

  const filteredWarehouses = useMemo(() => {
    if (!branchFilter) return warehouses;
    return warehouses.filter(w => {
      const parentId = w.parentLocation?._id || w.parentLocation;
      return String(parentId) === String(branchFilter);
    });
  }, [warehouses, branchFilter]);

  const assetFilteredWarehouses = useMemo(() => {
    if (!assetBranchFilter) return warehouses;
    return warehouses.filter(w => {
      const parentId = w.parentLocation?._id || w.parentLocation;
      return String(parentId) === String(assetBranchFilter);
    });
  }, [warehouses, assetBranchFilter]);

  // Load all locations once
  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminApi.getLocations({ limit: 1000 });
        const data = res.data?.data || res.data || [];
        setAllLocations(data);
        const map = {};
        data.forEach(loc => { map[loc._id] = loc; });
        setWarehouseMap(map);
      } catch {
        setAllLocations([]);
        setWarehouseMap({});
      }
    };
    load();
  }, []);

  // Debounce inventory search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  // Debounce asset search
  useEffect(() => {
    if (assetSearchTimer.current) clearTimeout(assetSearchTimer.current);
    assetSearchTimer.current = setTimeout(() => {
      setAssetDebouncedSearch(assetSearch);
      setAssetPagination(prev => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(assetSearchTimer.current);
  }, [assetSearch]);

  // ── Load inventory stock ──
  const loadStockLevels = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: pagination.page,
        limit: PAGE_SIZE,
        status: 'all',
        sortBy: 'item',
        sortOrder: 'asc',
      };
      if (warehouseFilter) params.warehouse = warehouseFilter;
      if (statusFilter === 'low_stock') params.lowStock = true;

      const res = await inventoryApi.getStockLevels(params);
      if (res.success) {
        let data = res.data || [];

        if (debouncedSearch) {
          const q = debouncedSearch.toLowerCase();
          data = data.filter(
            s =>
              s.item?.name?.toLowerCase().includes(q) ||
              s.item?.sku?.toLowerCase().includes(q)
          );
        }
        if (statusFilter === 'out_of_stock') {
          data = data.filter(s => s.quantity <= 0);
        }

        setStockLevels(data);
        setPagination(prev => ({
          ...prev,
          total: res.pagination?.total ?? data.length,
          pages: res.pagination?.pages ?? 1,
        }));
      } else {
        setError(res.message || 'Failed to load stock levels');
      }
    } catch {
      setError('Failed to load stock levels');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, warehouseFilter, statusFilter, debouncedSearch]);

  useEffect(() => {
    if (activeTab === 'inventory') loadStockLevels();
  }, [activeTab, loadStockLevels]);

  // ── Load asset stock ──
  const loadAssetLevels = useCallback(async () => {
    setAssetLoading(true);
    setAssetError('');
    try {
      const params = {
        page: assetPagination.page,
        limit: PAGE_SIZE,
      };
      if (assetWarehouseFilter) params.warehouse = assetWarehouseFilter;
      if (assetDebouncedSearch) params.search = assetDebouncedSearch;

      const res = await inventoryApi.getAssetStockLevels(params);
      if (res.success) {
        setAssetLevels(res.data || []);
        setAssetPagination(prev => ({
          ...prev,
          total: res.pagination?.total ?? (res.data || []).length,
          pages: res.pagination?.pages ?? 1,
        }));
      } else {
        setAssetError(res.message || 'Failed to load asset stock levels');
      }
    } catch {
      setAssetError('Failed to load asset stock levels');
    } finally {
      setAssetLoading(false);
    }
  }, [assetPagination.page, assetWarehouseFilter, assetDebouncedSearch]);

  useEffect(() => {
    if (activeTab === 'assets') loadAssetLevels();
  }, [activeTab, loadAssetLevels]);

  // Stock for an item can be split across multiple StockLevel documents (one per
  // location, e.g. separate GRN receipts into the same warehouse). Group them into a
  // single row per item+warehouse, summing quantities, so the same item doesn't appear
  // more than once in the table.
  const groupedStockLevels = useMemo(() => {
    const groups = new Map();
    for (const stock of stockLevels) {
      const itemId = stock.item?._id || stock.item;
      const warehouseId = stock.warehouse?._id || stock.warehouse;
      const key = `${itemId}__${warehouseId}`;
      const reserved = stock.reservedQuantity ?? 0;
      const available = stock.availableQuantity ?? ((stock.quantity || 0) - reserved);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          ...stock,
          quantity: stock.quantity || 0,
          reservedQuantity: reserved,
          availableQuantity: available,
          locationCount: 1,
        });
      } else {
        existing.quantity += stock.quantity || 0;
        existing.reservedQuantity += reserved;
        existing.availableQuantity += available;
        existing.locationCount += 1;
      }
    }
    return Array.from(groups.values());
  }, [stockLevels]);

  // Inventory summary counts
  const outOfStockCount = groupedStockLevels.filter(s => s.quantity <= 0).length;
  const lowStockCount = groupedStockLevels.filter(s => {
    const rp = s.item?.inventory?.reorderPoint;
    return s.quantity > 0 && rp && s.quantity <= rp;
  }).length;
  const inStockCount = groupedStockLevels.length - outOfStockCount - lowStockCount;

  // Asset summary counts
  const assetTotalQty = assetLevels.reduce((s, a) => s + (a.quantity || 0), 0);
  const assetTotalValue = assetLevels.reduce((s, a) => s + (a.cost?.totalCost || 0), 0);
  const assetTotalAllocated = assetLevels.reduce((s, a) => s + (a.allocatedQuantity || 0), 0);
  const assetTotalAvailable = assetLevels.reduce((s, a) => s + Math.max(0, (a.quantity || 0) - (a.allocatedQuantity || 0)), 0);

  const resolveLocation = (id) => {
    if (!id) return null;
    return warehouseMap[String(id)] || null;
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">Real-time stock levels across all locations</p>
        </div>
        <button
          onClick={() => activeTab === 'inventory' ? loadStockLevels() : loadAssetLevels()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'inventory'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Inventory Stock
          </span>
        </button>
        <button
          onClick={() => setActiveTab('assets')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'assets'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Fixed Assets Stock
          </span>
        </button>
      </div>

      {/* ── INVENTORY STOCK TAB ── */}
      {activeTab === 'inventory' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Items Tracked" value={pagination.total} color="blue" />
            <SummaryCard label="In Stock" value={inStockCount} color="green" />
            <SummaryCard label="Low Stock" value={lowStockCount} color="yellow" />
            <SummaryCard label="Out of Stock" value={outOfStockCount} color="red" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-lg p-3">
            {/* Search */}
            <div className="flex-1 min-w-48 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by item name or SKU..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Location (branch) filter */}
            {branches.length > 0 && (
              <select
                value={branchFilter}
                onChange={e => {
                  setBranchFilter(e.target.value);
                  setWarehouseFilter('');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white min-w-40"
              >
                <option value="">All Locations</option>
                {branches.map(b => (
                  <option key={b._id} value={b._id}>{b.name}{b.code ? ` (${b.code})` : ''}</option>
                ))}
              </select>
            )}

            {/* Warehouse filter */}
            <select
              value={warehouseFilter}
              onChange={e => { setWarehouseFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white min-w-44"
            >
              <option value="">All Warehouses</option>
              {filteredWarehouses.map(w => (
                <option key={w._id} value={w._id}>{w.name}{w.code ? ` (${w.code})` : ''}</option>
              ))}
            </select>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white min-w-40"
            >
              <option value="">All Status</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>

            {/* Clear filters */}
            {(searchInput || branchFilter || warehouseFilter || statusFilter) && (
              <button
                onClick={() => { setSearchInput(''); setBranchFilter(''); setWarehouseFilter(''); setStatusFilter(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                Clear
              </button>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Inventory Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {loading ? (
              <LoadingSpinner />
            ) : groupedStockLevels.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-sm font-medium">No stock records found</p>
                <p className="text-xs mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SKU</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Reserved</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Available</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Reorder Point</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupedStockLevels.map((stock, idx) => {
                      const item = stock.item || {};
                      const warehouseId = stock.warehouse?._id || stock.warehouse;
                      const warehouseObj = warehouseMap[String(warehouseId)] || null;
                      const parentLocation = warehouseObj?.parentLocation;
                      const parentName = typeof parentLocation === 'object' && parentLocation !== null
                        ? parentLocation.name
                        : (typeof parentLocation === 'string' ? warehouseMap[parentLocation]?.name : null);
                      const reorderPoint = item.inventory?.reorderPoint;
                      const unit = item.inventory?.unit || stock.unit || '';
                      return (
                        <tr key={stock._id || idx} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-medium text-gray-900 max-w-48 truncate" title={item.name}>
                            {item.name || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                            {item.sku || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {stock.locationCount > 1 ? 'Multiple' : (parentName || stock.location || '—')}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {warehouseObj?.name || '—'}
                            {warehouseObj?.code && <span className="ml-1 text-xs text-gray-400">({warehouseObj.code})</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {stock.quantity ?? 0}
                            {unit && <span className="ml-1 text-xs text-gray-400">{unit}</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">
                            {stock.reservedQuantity}
                          </td>
                          <td className="px-4 py-3 text-right text-blue-700 font-medium">
                            {stock.availableQuantity}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">
                            {reorderPoint ?? '—'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge quantity={stock.quantity ?? 0} reorderPoint={reorderPoint} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Inventory Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Page {pagination.page} of {pagination.pages} &nbsp;·&nbsp; {pagination.total} total records
              </span>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── FIXED ASSETS STOCK TAB ── */}
      {activeTab === 'assets' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Asset Lines" value={assetPagination.total} color="blue" />
            <SummaryCard label="Total Quantity" value={assetTotalQty} color="purple" />
            <SummaryCard label="Allocated" value={assetTotalAllocated} color="orange" sub="on this page" />
            <SummaryCard label="Available" value={assetTotalAvailable} color="teal" sub="on this page" />
            <SummaryCard
              label="Total Value"
              value={assetTotalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              color="green"
              sub="on this page"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-lg p-3">
            {/* Search */}
            <div className="flex-1 min-w-48 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by asset code or name..."
                value={assetSearch}
                onChange={e => setAssetSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Location (branch) filter */}
            {branches.length > 0 && (
              <select
                value={assetBranchFilter}
                onChange={e => {
                  setAssetBranchFilter(e.target.value);
                  setAssetWarehouseFilter('');
                  setAssetPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white min-w-40"
              >
                <option value="">All Locations</option>
                {branches.map(b => (
                  <option key={b._id} value={b._id}>{b.name}{b.code ? ` (${b.code})` : ''}</option>
                ))}
              </select>
            )}

            {/* Warehouse filter */}
            <select
              value={assetWarehouseFilter}
              onChange={e => { setAssetWarehouseFilter(e.target.value); setAssetPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white min-w-44"
            >
              <option value="">All Warehouses</option>
              {assetFilteredWarehouses.map(w => (
                <option key={w._id} value={w._id}>{w.name}{w.code ? ` (${w.code})` : ''}</option>
              ))}
            </select>

            {/* Clear filters */}
            {(assetSearch || assetBranchFilter || assetWarehouseFilter) && (
              <button
                onClick={() => { setAssetSearch(''); setAssetBranchFilter(''); setAssetWarehouseFilter(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                Clear
              </button>
            )}
          </div>

          {/* Error */}
          {assetError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              {assetError}
            </div>
          )}

          {/* Asset Stock Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {assetLoading ? (
              <LoadingSpinner />
            ) : assetLevels.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="text-sm font-medium">No fixed asset stock records found</p>
                <p className="text-xs mt-1">Fixed asset stock is created when FIXED_ASSET GRNs are approved</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Asset Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Asset Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Allocated</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Available</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Unit Cost</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {assetLevels.map((asset, idx) => {
                      const warehouseId = asset.warehouse?._id || asset.warehouse;
                      const locationId = asset.location?._id || asset.location;
                      const warehouseObj = resolveLocation(warehouseId);
                      const locationObj = resolveLocation(locationId);
                      const parentLocation = warehouseObj?.parentLocation;
                      const parentName = typeof parentLocation === 'object' && parentLocation !== null
                        ? parentLocation.name
                        : (typeof parentLocation === 'string' ? warehouseMap[parentLocation]?.name : null);
                      const displayLocation = locationObj?.name || parentName || '—';
                      const allocatedQty = asset.allocatedQuantity ?? 0;
                      const availableQty = Math.max(0, (asset.quantity ?? 0) - allocatedQty);
                      return (
                        <tr key={asset._id || idx} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-mono text-xs text-gray-600">
                            {asset.assetCode || '—'}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900 max-w-56 truncate" title={asset.assetName}>
                            {asset.assetName || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {displayLocation}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {warehouseObj?.name || '—'}
                            {warehouseObj?.code && <span className="ml-1 text-xs text-gray-400">({warehouseObj.code})</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {asset.quantity ?? 0}
                          </td>
                          <td className="px-4 py-3 text-right text-orange-600">
                            {allocatedQty}
                          </td>
                          <td className={`px-4 py-3 text-right font-medium ${availableQty > 0 ? 'text-teal-700' : 'text-red-600'}`}>
                            {availableQty}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {asset.cost?.unitCost != null
                              ? asset.cost.unitCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-green-700">
                            {asset.cost?.totalCost != null
                              ? asset.cost.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Asset Pagination */}
          {assetPagination.pages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Page {assetPagination.page} of {assetPagination.pages} &nbsp;·&nbsp; {assetPagination.total} total records
              </span>
              <div className="flex gap-2">
                <button
                  disabled={assetPagination.page <= 1}
                  onClick={() => setAssetPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  disabled={assetPagination.page >= assetPagination.pages}
                  onClick={() => setAssetPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StockOverview;
